import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Loader2, ArrowLeft, ShieldCheck, MessageCircle } from "lucide-react";
import { api, setSessionToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TermsAcceptCheckbox from "./TermsAcceptCheckbox";

/**
 * PhoneAuthNative — phone-number + OTP signup & login.
 * Uses Pingbix (SMS + WhatsApp fallback) via `/api/auth/phone/*` endpoints.
 *
 * Props:
 *   redirectTo   — where to navigate on success (default `/app`)
 *   onSuccess    — optional callback (data) instead of navigating. Used
 *                  by the Follow modal to keep the user on the creator page.
 *   initialMode  — 'signup' | 'login' (default 'login')
 *   compact      — bool. Trims the top helper text for embedded contexts.
 *
 * All interactive elements carry data-testid attributes for testing.
 */
export default function PhoneAuthNative({
  redirectTo = "/app",
  onSuccess,
  initialMode = "login",
  onModeChange,
  compact = false,
}) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  // Normalize: EmailPasswordAuthNative uses `signin`, we use `login`. Accept both.
  const normalizedInitial = initialMode === "signin" ? "login" : initialMode;
  const [mode, setModeState] = useState(normalizedInitial); // 'login' | 'signup' | 'verify' | 'complete_signup'
  // v145.39 — Bubble mode changes up so AuthTabs can share the state
  // with the sibling Email component when the user switches tabs.
  const setMode = (next) => {
    setModeState(next);
    if (next === "login" || next === "signup") onModeChange?.(next);
  };
  // Re-sync when the parent (AuthTabs) hands us a fresh initialMode
  // — e.g. after switching from Email tab where the user just toggled
  // Sign in / Create account.
  useEffect(() => {
    const target = initialMode === "signin" ? "login" : initialMode;
    if ((target === "login" || target === "signup") && target !== mode) {
      setModeState(target);
    }
  }, [initialMode]);
  const [countryCode, setCountryCode] = useState("91");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  // v146.12 — Terms acceptance is gated by AuthPage's top-level 13+
  // checkbox; user can't reach this form until that's ticked.
  const [terms] = useState(true);
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [channel, setChannel] = useState(""); // whatsapp | sms
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [savedPurpose, setSavedPurpose] = useState(normalizedInitial); // frozen at start

  const timerRef = useRef(null);
  useEffect(() => {
    if (resendIn <= 0) return;
    timerRef.current = setInterval(
      () => setResendIn((s) => (s > 0 ? s - 1 : 0)),
      1000,
    );
    return () => clearInterval(timerRef.current);
  }, [resendIn]);

  const finishAuth = (data) => {
    if (data?.session_token) setSessionToken(data.session_token);
    if (data?.user) setUser(data.user);
    toast.success(
      `Welcome${data?.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}!`,
    );
    if (onSuccess) {
      onSuccess(data);
      return;
    }
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      window.location.assign(redirectTo);
    } else {
      navigate(redirectTo, { replace: true });
    }
  };

  const startOtp = async (purpose) => {
    if (!/^\d{6,15}$/.test(phone) || (countryCode === "91" && phone.length !== 10)) {
      toast.error(countryCode === "91"
        ? "Enter a valid 10-digit mobile number."
        : "Enter a valid phone number (digits only).");
      return;
    }
    // v146.11 — Name is optional; collected during post-signup onboarding.
    if (purpose === "signup" && !terms) {
      toast.error("Please accept the Terms & Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/start", {
        country_code: countryCode,
        phone,
        channel: "auto",
        purpose,
      });
      setChallengeId(data.challenge_id);
      setChannel(data.channel || "");
      setResendIn(data.resend_available_in_seconds || 30);
      setSavedPurpose(purpose);
      setMode("verify");
      toast.success(
        `Code sent via ${data.channel === "whatsapp" ? "WhatsApp" : "SMS"} to +${countryCode} ${phone}`,
      );
    } catch (err) {
      toast.error(formatDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify", {
        challenge_id: challengeId,
        code: otp,
        name: savedPurpose === "signup" ? name.trim() : undefined,
        terms_accepted: savedPurpose === "signup" ? terms : undefined,
        referral_code: localStorage.getItem("ref_code") || undefined,
      });
      // v145.12 — Unified sign-in/sign-up: if login flow resolved to
      // an unknown number, backend returns needs_signup=true and keeps
      // the challenge alive for 10 minutes. Ask for name + T&C and
      // POST to /auth/phone/complete-signup.
      if (data?.needs_signup) {
        setMode("complete_signup");
        return;
      }
      finishAuth(data);
    } catch (err) {
      toast.error(formatDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCompleteSignup = async (e) => {
    e.preventDefault();
    // v146.11 — Name is optional; collected during post-signup onboarding.
    if (!terms) { toast.error("Please accept the Terms & Privacy Policy to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/complete-signup", {
        challenge_id: challengeId,
        terms_accepted: terms,
        referral_code: localStorage.getItem("ref_code") || undefined,
      });
      finishAuth(data);
    } catch (err) {
      toast.error(formatDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async (channelOverride) => {
    if (resendIn > 0 || !challengeId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/resend", {
        challenge_id: challengeId,
        ...(channelOverride ? { channel: channelOverride } : {}),
      });
      setResendIn(data.resend_available_in_seconds || 30);
      setOtp("");
      if (data.channel) setChannel(data.channel);
      toast.success(
        data.channel === "whatsapp"
          ? "Sent a fresh code on WhatsApp."
          : data.channel === "sms"
          ? "Sent a fresh code via SMS."
          : "Sent a fresh code.",
      );
    } catch (err) {
      toast.error(formatDetail(err));
    } finally {
      setBusy(false);
    }
  };

  // ─── Complete-signup step (existing user missing name/terms) ────────
  if (mode === "complete_signup") {
    return (
      <form
        onSubmit={submitCompleteSignup}
        className="w-full space-y-3"
        data-testid="phone-auth-complete-signup-form"
      >
        <div className="text-center py-2">
          <div className="inline-flex items-center justify-center w-12 h-12 border-2 border-ink bg-mint mb-2">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h3 className="font-black text-base">Welcome! Let&apos;s finish setting up</h3>
          <p className="text-xs text-ink/70 mt-1">
            We couldn&apos;t find an account for <span className="font-bold text-ink">+{countryCode} {phone}</span> —
            no worries, we&apos;ll create one now.
          </p>
        </div>
        {/* v146.11 — Name input removed; asked in step 1 of onboarding.
            v146.12 — Terms checkbox removed; gated by AuthPage top-level. */}
        <button
          type="submit"
          disabled={busy}
          data-testid="phone-complete-signup-submit"
          className="w-full border-2 border-ink bg-coral text-white px-4 py-3 font-black text-base shadow-brutalSm inline-flex items-center justify-center gap-2 hover:translate-y-[1px] disabled:opacity-60"
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          Create my account
        </button>
      </form>
    );
  }

  // ─── Verify step ─────────────────────────────────────────────────────
  if (mode === "verify") {
    return (
      <form
        onSubmit={submitVerify}
        className="w-full space-y-3"
        data-testid="phone-auth-verify-form"
      >
        <button
          type="button"
          onClick={() => setMode(savedPurpose)}
          data-testid="phone-verify-back"
          className="inline-flex items-center gap-1 text-xs font-bold text-ink/70 hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> Change number
        </button>
        <div className="text-center py-2">
          <div className="inline-flex items-center justify-center w-12 h-12 border-2 border-ink bg-mint mb-2">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h3 className="font-black text-base">Enter the 6-digit code</h3>
          <p className="text-xs text-ink/70 mt-1">
            Sent via {channel === "whatsapp" ? "WhatsApp" : "SMS"} to{" "}
            <span className="font-bold text-ink">+{countryCode} {phone}</span>
          </p>
        </div>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="6-digit code"
          data-testid="phone-otp-input"
          autoComplete="one-time-code"
          required
          autoFocus
          className="w-full border-2 border-ink px-3 py-3 text-lg font-mono font-black text-center tracking-[0.4em] bg-white"
        />
        <button
          type="submit"
          disabled={busy || otp.length !== 6}
          data-testid="phone-verify-submit"
          className="w-full border-2 border-ink bg-coral text-white px-4 py-3 font-black text-base shadow-brutalSm inline-flex items-center justify-center gap-2 hover:translate-y-[1px] disabled:opacity-60"
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          {savedPurpose === "signup" ? "Verify & create account" : "Verify & sign in"}
        </button>
        <div className="text-center text-[11px] font-bold pt-1 space-y-2">
          <div>
            Didn&apos;t get it?{" "}
            <button
              type="button"
              onClick={() => handleResend()}
              disabled={resendIn > 0 || busy}
              data-testid="phone-resend-otp"
              className="underline hover:text-coral disabled:no-underline disabled:opacity-50"
            >
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
            </button>
          </div>
          {/* v147.99.27 — WhatsApp backup channel. Shown when the current
              channel is SMS (or unknown) so a user who never got the
              text can pull an OTP via WhatsApp instead. Same 30s
              cooldown as regular resend. */}
          {channel !== "whatsapp" && (
            <button
              type="button"
              onClick={() => handleResend("whatsapp")}
              disabled={resendIn > 0 || busy}
              data-testid="phone-resend-otp-whatsapp"
              className="inline-flex items-center justify-center gap-1.5 border-2 border-ink bg-mint px-3 py-1.5 shadow-brutalSm text-[11px] font-black hover:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageCircle className="w-3 h-3" strokeWidth={2.5} />
              {resendIn > 0 ? `Try WhatsApp in ${resendIn}s` : "Send on WhatsApp instead"}
            </button>
          )}
          {channel === "whatsapp" && (
            <button
              type="button"
              onClick={() => handleResend("sms")}
              disabled={resendIn > 0 || busy}
              data-testid="phone-resend-otp-sms"
              className="inline-flex items-center justify-center gap-1.5 border-2 border-ink bg-white px-3 py-1.5 shadow-brutalSm text-[11px] font-black hover:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Phone className="w-3 h-3" strokeWidth={2.5} />
              {resendIn > 0 ? `Try SMS in ${resendIn}s` : "Send via SMS instead"}
            </button>
          )}
        </div>
      </form>
    );
  }

  // ─── Send-OTP step (signup or login) ────────────────────────────────
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); startOtp(mode); }}
      className="w-full space-y-2"
      data-testid={`phone-auth-${mode}-form`}
    >
      {!compact && (
        <p className="text-sm text-ink/70 font-medium">
          {mode === "signup"
            ? "Sign up with your phone number. We'll text you a 6-digit code."
            : "Sign in with your registered phone number."}
        </p>
      )}
      {/* v146.11 — Name is collected in the 3-step onboarding after
          signup, not here. Keeps the auth form to the bare minimum. */}
      {/* Country + phone row */}
      <div className="flex gap-2 items-stretch">
        <div className="relative w-28 shrink-0">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base font-bold pointer-events-none">
            +
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="91"
            data-testid="phone-country-code-input"
            maxLength={4}
            required
            className="w-full border-2 border-ink pl-6 pr-2 py-3 text-base font-bold bg-white text-center"
          />
        </div>
        <div className="relative flex-1 min-w-0">
          <Phone className="w-5 h-5 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 15))}
            placeholder={countryCode === "91" ? "10-digit mobile number" : "Phone number"}
            data-testid="phone-number-input"
            autoComplete="tel"
            required
            maxLength={countryCode === "91" ? 10 : 15}
            className="w-full border-2 border-ink pl-10 pr-3 py-3 text-base bg-white"
          />
        </div>
      </div>
      {/* v146.12 — Inner terms checkbox removed; AuthPage gates auth
          methods behind a single top-level 13+ acknowledgement. */}
      <button
        type="submit"
        disabled={busy}
        data-testid={`phone-submit-${mode}`}
        className="w-full border-2 border-ink bg-coral text-white px-4 py-3 font-black text-base shadow-brutalSm inline-flex items-center justify-center gap-2 hover:translate-y-[1px] disabled:opacity-60"
      >
        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
        {mode === "signup" ? "Send code & sign up" : "Send OTP"}
      </button>
      <div className="flex justify-center pt-2">
        {mode === "login" ? (
          <button
            type="button"
            onClick={() => setMode("signup")}
            data-testid="phone-goto-signup"
            className="text-sm font-bold text-ink/80 hover:underline hover:text-ink"
          >
            New here? <span className="text-ink font-black">Create an account</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setMode("login")}
            data-testid="phone-goto-login"
            className="text-sm font-bold text-ink/80 hover:underline hover:text-ink"
          >
            Already have an account? <span className="text-ink font-black">Sign in</span>
          </button>
        )}
      </div>
    </form>
  );
}

function formatDetail(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ") || "Check your inputs and try again.";
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Try again.";
}
